import { CheckCircle2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Avatar } from '../primitives/avatar';
import { ProgressRing } from './charts';

export interface AuthPanel {
  title: string;
  text: string;
  /** Short reasons to trust the platform, shown with a check mark */
  points?: string[];
  /** Small print at the bottom of the panel */
  footnote?: string;
}

interface AuthShowcaseProps {
  /** A sample proposal, e.g. { company: 'Måleri Väst AB', price: '18 500 kr', status: 'Accepted' } */
  proposal: { company: string; label: string; price: string; status: string };
  /** A chat line from the company */
  message: { from: string; text: string };
  /** A ring with a short caption, e.g. 3 of 3 proposals in */
  progress: { label: string; value: number; max: number };
  /** Names drawn as a row of company avatars, with a caption */
  companies: { names: string[]; caption: string };
}

/**
 * The picture on the signed-out pages: a few product pieces (a proposal, a chat line, progress) floating on the
 * navy panel, drawn with the real components so it always matches the app. Decorative; screen readers skip it.
 */
export function AuthShowcase({ proposal, message, progress, companies }: AuthShowcaseProps) {
  return (
    <div aria-hidden className="relative h-72 w-full max-w-md select-none text-foreground">
      <div className="absolute left-0 top-0 w-72 -rotate-2 rounded-xl bg-surface p-4 shadow-md">
        <div className="flex items-center gap-3">
          <Avatar name={proposal.company} kind="company" size="md" />
          <div className="grid min-w-0 flex-1">
            <span className="truncate text-sm font-semibold">{proposal.company}</span>
            <span className="text-xs text-muted-foreground">{proposal.label}</span>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-lg font-bold tabular-nums">{proposal.price}</span>
          <span className="rounded-full bg-success/15 px-2 py-0.5 text-xs font-semibold text-success">
            {proposal.status}
          </span>
        </div>
      </div>
      <div className="absolute right-0 top-28 w-64 rotate-1 rounded-xl bg-surface p-3 shadow-md">
        <div className="flex items-start gap-2">
          <Avatar name={message.from} size="sm" />
          <div className="rounded-xl rounded-tl-sm bg-muted px-3 py-2 text-xs leading-relaxed">
            {message.text}
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-8 flex items-center gap-3 rounded-xl bg-surface p-3 pr-4 shadow-md">
        <ProgressRing label={progress.label} value={progress.value} max={progress.max} size="sm" color={3} />
        <span className="text-xs font-medium">{progress.label}</span>
      </div>
      <div className="absolute bottom-3 right-2 grid justify-items-end gap-1.5">
        <div className="flex -space-x-2">
          {companies.names.map((name) => (
            <Avatar key={name} name={name} kind="company" size="sm" className="ring-2 ring-sidebar" />
          ))}
        </div>
        <span className="text-xs text-sidebar-muted">{companies.caption}</span>
      </div>
    </div>
  );
}

interface AuthLayoutProps {
  /** The full logo for light backgrounds; shown above the form on phones */
  brand: ReactNode;
  /** The logo for the navy panel, e.g. `<Logo tone="light" />` */
  brandOnDark?: ReactNode;
  /** Top right, e.g. the language switch */
  actions?: ReactNode;
  /** The brand panel on the left from `lg`; without it the form is centred alone */
  panel?: AuthPanel;
  /** Under the panel text, e.g. <AuthShowcase /> */
  showcase?: ReactNode;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  /** Under the form, e.g. "Back to login" */
  footer?: ReactNode;
  className?: string;
}

/**
 * Login, password and other signed-out pages: a navy brand panel on the left (desktop) and the form on the right.
 * On phones the panel is hidden and the logo sits above the form.
 */
export function AuthLayout({
  brand,
  brandOnDark,
  actions,
  panel,
  showcase,
  title,
  description,
  children,
  footer,
  className,
}: AuthLayoutProps) {
  return (
    <div className="flex min-h-dvh bg-background">
      {panel && (
        <aside className="relative hidden w-[44%] max-w-2xl shrink-0 overflow-hidden bg-sidebar p-10 text-sidebar-foreground lg:flex lg:flex-col xl:p-14">
          {/* Soft brand-coloured light, drawn from theme tokens */}
          <div
            aria-hidden
            className="pointer-events-none absolute -right-32 -top-32 size-96 rounded-full bg-primary/15 blur-3xl"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-40 -left-24 size-[28rem] rounded-full bg-sidebar-accent blur-3xl"
          />
          <div className="relative">{brandOnDark ?? brand}</div>
          <div className="relative my-auto grid max-w-md gap-4 py-10">
            <h2 className="text-4xl leading-tight font-bold tracking-tight text-balance">{panel.title}</h2>
            <p className="text-lg leading-relaxed text-sidebar-muted">{panel.text}</p>
            {panel.points && (
              <ul className="mt-1 grid gap-2.5">
                {panel.points.map((point) => (
                  <li key={point} className="flex items-start gap-3 text-sm">
                    <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            )}
            {/* Only on screens tall enough to show it without pushing the text off screen */}
            {showcase && <div className="mt-6 hidden [@media(min-height:52rem)]:block">{showcase}</div>}
          </div>
          {panel.footnote && <p className="relative text-sm text-sidebar-muted">{panel.footnote}</p>}
        </aside>
      )}
      <div className="flex min-w-0 flex-1 flex-col px-5 py-6 sm:px-10">
        <header className="flex items-center justify-between gap-4">
          <div className={cn(panel && 'lg:invisible')}>{brand}</div>
          {actions}
        </header>
        <main className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center py-8">
          <div
            className={cn(
              'grid gap-5 rounded-xl border border-border bg-surface p-6 shadow-md sm:p-8',
              className,
            )}
          >
            <div className="grid gap-1.5">
              <h1 className="text-2xl font-bold tracking-tight text-balance sm:text-3xl">{title}</h1>
              {description && <div className="text-sm text-muted-foreground">{description}</div>}
            </div>
            {children}
            {footer && (
              <div className="border-t border-border pt-4 text-sm text-muted-foreground">{footer}</div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
